<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <div
        class="target-practice focusable"
        id="target-practice-container"
        tabindex="0"
        slot="content"
      >
        <div
          v-if="progress"
          v-show="showBothHands || holdKeyPressed"
          class="target-practice-container"
        >
          <div v-if="isVisibleLeftHand" class="target-practice-image-left">
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="leftHand"></svg>
            </div>
          </div>

          <div v-if="isVisibleRightHand" class="target-practice-image-right">
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="rightHand"></svg>
            </div>
          </div>
        </div>
      </div>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';

import ActivityStage from 'components/activity/ActivityStage.vue';

// Import left hand images
import LHEmpty from 'assets/svg/target-practice/hand-pfc-left-empty.svg';
// Row1
import LHPinkyYellow from 'assets/svg/target-practice/left-hand/left-hand-pinky-yellow.svg';
import LHRingYellow from 'assets/svg/target-practice/left-hand/left-hand-ring-yellow.svg';
import LHMiddleYellow from 'assets/svg/target-practice/left-hand/left-hand-middle-yellow.svg';
import LHIndexYellow from 'assets/svg/target-practice/left-hand/left-hand-index-yellow.svg';
import LHIndexExtraYellow from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-yellow.svg';
// Row2
import LHPinkyGreen from 'assets/svg/target-practice/left-hand/left-hand-pinky-green.svg';
import LHRingGreen from 'assets/svg/target-practice/left-hand/left-hand-ring-green.svg';
import LHMiddleGreen from 'assets/svg/target-practice/left-hand/left-hand-middle-green.svg';
import LHIndexGreen from 'assets/svg/target-practice/left-hand/left-hand-index-green.svg';
import LHIndexExtraGreen from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-green.svg';
// Row3
import LHPinkyBlue from 'assets/svg/target-practice/left-hand/left-hand-pinky-blue.svg';
import LHRingBlue from 'assets/svg/target-practice/left-hand/left-hand-ring-blue.svg';
import LHMiddleBlue from 'assets/svg/target-practice/left-hand/left-hand-middle-blue.svg';
import LHIndexBlue from 'assets/svg/target-practice/left-hand/left-hand-index-blue.svg';
import LHIndexExtraBlue from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-blue.svg';

// Import right hand images
import RHEmpty from 'assets/svg/target-practice/hand-pfc-right-empty.svg';

// Row1
import RHIndexYellow from 'assets/svg/target-practice/right-hand/right-hand-index-yellow.svg';
import RHMiddleYellow from 'assets/svg/target-practice/right-hand/right-hand-middle-yellow.svg';
import RHRingYellow from 'assets/svg/target-practice/right-hand/right-hand-ring-yellow.svg';
import RHPinkyYellow from 'assets/svg/target-practice/right-hand/right-hand-pinky-yellow.svg';
import RHIndexExtraYellow from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-yellow.svg';

// Row2
import RHIndexGreen from 'assets/svg/target-practice/right-hand/right-hand-index-green.svg';
import RHMiddleGreen from 'assets/svg/target-practice/right-hand/right-hand-middle-green.svg';
import RHRingGreen from 'assets/svg/target-practice/right-hand/right-hand-ring-green.svg';
import RHPinkyGreen from 'assets/svg/target-practice/right-hand/right-hand-pinky-green.svg';
import RHIndexExtraGreen from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-green.svg';

// Row3
import RHIndexBlue from 'assets/svg/target-practice/right-hand/right-hand-index-blue.svg';
import RHMiddleBlue from 'assets/svg/target-practice/right-hand/right-hand-middle-blue.svg';
import RHRingBlue from 'assets/svg/target-practice/right-hand/right-hand-ring-blue.svg';
import RHPinkyBlue from 'assets/svg/target-practice/right-hand/right-hand-pinky-blue.svg';
import RHIndexExtraBlue from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-blue.svg';

export default {
  mixins: [ActivityManager, TypingActivityHelper, TouchAndHoldHelper],
  components: {
    ActivityStage
  },
  data() {
    return {
      activityName: 'Target Practice',
      showBothHands: false,
      displayHand: '',
      hands: {
        left: {
          images: {
            q: LHPinkyYellow,
            w: LHRingYellow,
            e: LHMiddleYellow,
            r: LHIndexYellow,
            t: LHIndexExtraYellow,
            a: LHPinkyGreen,
            s: LHRingGreen,
            d: LHMiddleGreen,
            f: LHIndexGreen,
            g: LHIndexExtraGreen,
            z: LHPinkyBlue,
            x: LHRingBlue,
            c: LHMiddleBlue,
            v: LHIndexBlue,
            b: LHIndexExtraBlue,
            empty: LHEmpty
          }
        },
        right: {
          images: {
            y: RHIndexExtraYellow,
            u: RHIndexYellow,
            i: RHMiddleYellow,
            o: RHRingYellow,
            p: RHPinkyYellow,
            h: RHIndexExtraGreen,
            j: RHIndexGreen,
            k: RHMiddleGreen,
            l: RHRingGreen,
            ';': RHPinkyGreen,
            n: RHIndexExtraBlue,
            m: RHIndexBlue,
            ',': RHMiddleBlue,
            '.': RHRingBlue,
            '/': RHPinkyBlue,
            empty: RHEmpty
          }
        }
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    isVisibleLeftHand() {
      return this.showHand('left');
    },
    leftHand() {
      return this.getHand('left', this.expectedKey);
    },
    isVisibleRightHand() {
      return this.showHand('right');
    },
    rightHand() {
      return this.getHand('right', this.expectedKey);
    }
  },

  methods: {
    showHand(hand) {
      return this.showBothHands || this.displayHand === hand;
    },
    async loadActivity() {
      this.createRounds(this.getActivityState());
    },
    getKeyboardConfig() {
      const { characters } = this.activityData.keyboard;

      const {
        keyboard: { keyboardType, roofs }
      } = this.activityData;
      return {
        keys: characters,
        touchAndHold: this.isTouchHold,
        showTargets: true,
        keyboardType,
        roofs
      };
    },

    stepCallback() {
      return this.currentStep;
    },

    getActivityState() {
      const { rounds } = this.activityData;

      const voiceFile = `targetpractice${
        this.isTouchHold ? '' : 'nth'
      }-${this.getRandomVoice()}-1.mp3`;

      return {
        rounds,
        stepIndicator: 'keystrokes',
        instructions: {
          written: this.isTouchHold
            ? 'Hold the first target. Then type on the new targets.'
            : 'Type on the targets.',
          voice: `${this.voiceInstructionsPrefix}/${voiceFile}`
        }
      };
    },

    startRound() {
      this.enableKeyboard();
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.setActivityContainerHeight();

      if (this.isTouchHold) {
        this.displayHand = this.targetKey === 'j' ? 'left' : 'right';
      } else {
        this.showBothHands = true;
        this.displayHand = '';
      }
    },

    getHand(side, key) {
      const handImages = this.hands[side].images;

      return handImages[key] || handImages.empty;
    },

    handleCorrectKey(attempt) {
      document.getElementById('target-practice-container')?.blur();
      this.captureKeyboardAttemptSegment(attempt, true);

      if (this.isLastStepInRound) {
        this.finishRound();
        return;
      }

      this.sndType();
      this.setCurrentStep({ stepCallback: this.stepCallback });
    },

    finishRound() {
      this.sndBell();

      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.startRound();
      }
    }
  },
  watch: {
    expectedKey(newVal) {
      if (newVal) {
        let instructions = `Type the ${newVal} key.`;

        this.setCurrentKeyboardingSegment('');
        this.setTotalKeyboardingSegment(newVal);
        this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#target-practice-container {
  width: 100%;
  height: 100%;
}

.target-practice-container {
  position: relative;
  max-width: 1024px;
  max-height: 480px;
  width: 100%;
  height: 100%;

  .target-practice-image-left,
  .target-practice-image-right {
    position: absolute;
    top: 8%;
    width: 200px;
    height: 250px;
    max-height: 100%;

    .svg-image-container {
      position: absolute;
      height: 100%;
      visibility: hidden;

      svg {
        height: 100%;
      }
    }

    .current-letter {
      visibility: visible;
    }
  }

  .target-practice-image-left {
    left: 100px;
    @media (max-width: 690px) {
      left: 20px;
    }
  }

  .target-practice-image-right {
    right: 100px;
    @media (max-width: 690px) {
      right: 20px;
    }
  }
}
</style>
